import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role: "property_manager" });
      toast.success("Account created. Pick how you'll use ScanMint.");
      nav("/onboarding/business-type");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#09090B] text-white relative overflow-hidden">
        <Link
          to="/"
          className="inline-block bg-white rounded-xl p-3 shadow-sm self-start"
          data-testid="register-brand"
        >
          <img
            src="/scanmint-logo.png"
            alt="ScanMint"
            className="h-12 w-auto block"
            draggable={false}
          />
        </Link>
        <div>
          <div className="mono-tiny text-white/50 mb-3">Create your property</div>
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Three steps to a verified property.
          </h2>
          <ol className="mt-6 space-y-4 text-sm">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0F8F4F] text-white text-xs flex items-center justify-center font-bold">
                1
              </span>
              <span className="text-white/80">Create an account &amp; set up your property</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center font-bold">
                2
              </span>
              <span className="text-white/80">Add tenants &amp; vehicles</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-white/10 text-white text-xs flex items-center justify-center font-bold">
                3
              </span>
              <span className="text-white/80">Activate QR stickers · verify instantly</span>
            </li>
          </ol>
        </div>
        <div className="mono-tiny text-white/40">© 2026 ScanMint</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="register-form">
          <div className="md:hidden mb-4">
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-12 w-auto select-none"
              draggable={false}
            />
          </div>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Create your account
            </h1>
            <p className="text-sm text-[#71717A] mt-2">Sign up as a property manager.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="mono-tiny text-[#71717A]">
                Full name
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12"
                data-testid="register-name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="mono-tiny text-[#71717A]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-12"
                data-testid="register-email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="mono-tiny text-[#71717A]">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-12 pr-12"
                  data-testid="register-password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#09090B] transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-[#0F8F4F]/40"
                  data-testid="register-toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" strokeWidth={1.75} />
                  ) : (
                    <Eye className="w-4 h-4" strokeWidth={1.75} />
                  )}
                </button>
              </div>
              <div className="text-xs text-[#71717A]">Minimum 6 characters.</div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-base"
            data-testid="register-submit"
          >
            {loading ? "Creating…" : "Create account"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>

          <div className="text-sm text-[#71717A]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0F8F4F] font-medium" data-testid="register-to-login">
              Sign in
            </Link>
          </div>

          <div
            className="text-xs text-[#71717A] leading-relaxed pt-2 border-t border-[#E4E4E7]"
            data-testid="register-legal-notice"
          >
            By creating an account, you agree to ScanMint&apos;s{" "}
            <Link
              to="/legal/terms"
              className="text-[#0F8F4F] hover:underline"
              data-testid="register-link-terms"
            >
              Terms of Service
            </Link>
            ,{" "}
            <Link
              to="/legal/privacy"
              className="text-[#0F8F4F] hover:underline"
              data-testid="register-link-privacy"
            >
              Privacy Policy
            </Link>
            , and{" "}
            <Link
              to="/legal/refund"
              className="text-[#0F8F4F] hover:underline"
              data-testid="register-link-refund"
            >
              Refund &amp; Cancellation Policy
            </Link>
            .
          </div>
        </form>
      </div>
    </div>
  );
}
